import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  School as SchoolIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import Layout from '../../components/layout';
import Header from '../../components/header';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseconf';

export default function MyClasses() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedClass, setSelectedClass] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [classStudents, setClassStudents] = useState([]);

  useEffect(() => {
    fetchTeacherClasses();
  }, []);

  const fetchTeacherClasses = async () => {
    try {
      setLoading(true);
      const loggedUser = JSON.parse(sessionStorage.getItem('logged_user'));

      if (!loggedUser || !loggedUser.institution_id || loggedUser.role !== 'teacher') {
        setError('Unauthorized access');
        setLoading(false);
        return;
      }

      const db_instance = require('../../firebaseconf').db;
      
      // Fetch classes assigned to this teacher
      const classesRef = collection(db_instance, 'institutions', loggedUser.institution_id, 'classes');
      const classesQuery = query(classesRef, where('teacher_id', '==', loggedUser.id));
      const classesSnapshot = await getDocs(classesQuery);

      const classesData = classesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setClasses(classesData);
    } catch (err) {
      console.error('Error fetching classes:', err);
      setError('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (classData) => {
    try {
      setSelectedClass(classData);
      const loggedUser = JSON.parse(sessionStorage.getItem('logged_user'));

      // Fetch students in this class
      const studentsRef = collection(db, 'institutions', loggedUser.institution_id, 'students');
      const studentsQuery = query(studentsRef, where('class', '==', classData.name));
      const studentsSnapshot = await getDocs(studentsQuery);

      const students = studentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setClassStudents(students);
      setDetailsOpen(true);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to load students');
    }
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedClass(null);
    setClassStudents([]);
  };

  if (loading) {
    return (
      <Layout>
        <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="My Classes" />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            📚 Your Assigned Classes
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage your classes and view student details
          </Typography>
        </Box>

        {classes.length === 0 ? (
          <Card sx={{ p: 3, textAlign: 'center' }}>
            <SchoolIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#666' }}>
              No classes assigned yet
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Contact your institution admin to assign classes
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {classes.map((classData) => (
              <Grid item xs={12} md={6} lg={4} key={classData.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SchoolIcon sx={{ color: '#2196F3', mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {classData.name}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      <strong>Section:</strong> {classData.section || 'N/A'}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      <strong>Room:</strong> {classData.room_number || 'N/A'}
                    </Typography>

                    <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                      <strong>Level:</strong> {classData.level || 'N/A'}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDetails(classData)}
                      >
                        View Students
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Class Details Dialog */}
        <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
          <DialogTitle>
            Class Details: {selectedClass?.name}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {classStudents.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#999' }}>
                No students in this class yet
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Email</strong></TableCell>
                      <TableCell><strong>Roll No</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {classStudents.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell>{student.name}</TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell>{student.roll_number || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip
                            label={student.status || 'active'}
                            size="small"
                            color={student.status === 'active' ? 'success' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetails}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
}
